import React from 'react';

/**
 * The header's location control: which store the user is acting as (the ownership rules key on
 * it), and the way to change it. Tablet pass 2026-10-20, item 11.
 *
 * It always shows a label. Below the shell's mobile breakpoint (`compact`: MainLayout.jsx
 * MOBILE_BREAKPOINT, under 1,024 px) the full name does not fit beside the bar's buttons, so it
 * shows the store code (`locations.code`, the code the Customers table chip shows), or, for a
 * location with no code, the name cut with an ellipsis. Below 1,202 px (`roomy` false) the
 * "Working at:" prefix goes and the control is a 44 px target. The full name is always in the
 * accessible name and the tooltip.
 *
 * Props: location {id, name, code?, color?}; compact; roomy; onClick.
 */
export function locationLabel(location) {
  const name = (location && location.name) || '';
  const code = String((location && location.code) || '').trim().toUpperCase();
  return { name, code, accessible: `Working at: ${name}${code ? ` (${code})` : ''}. Change location` };
}

export default function HeaderLocationButton({ location, compact, roomy, onClick }) {
  const { name, code, accessible } = locationLabel(location);
  const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <button
      type="button"
      onClick={onClick}
      title={accessible}
      aria-label={accessible}
      data-testid="header-location"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        padding: '4px 10px 4px 8px', borderRadius: roomy ? '16px' : '22px',
        minHeight: roomy ? undefined : '44px', minWidth: roomy ? undefined : '44px',
        border: '1px solid var(--border-color)', background: 'var(--bg-elevated)',
        cursor: 'pointer', fontSize: '12px', fontWeight: 600,
        color: 'var(--text-secondary)', transition: 'all 0.15s ease',
        marginLeft: '4px'
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '8px', height: '8px', borderRadius: '50%',
          background: location.color || 'var(--text-tertiary)', flexShrink: 0
        }}
      />
      {!compact && roomy && (
        <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>Working at:</span>
      )}
      {compact && code ? (
        <span data-label="code" style={{ letterSpacing: '0.04em' }}>{code}</span>
      ) : (
        <span data-label="name" style={compact ? { ...ellipsis, maxWidth: '120px' } : undefined}>
          {name}
        </span>
      )}
    </button>
  );
}
